import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import AgentSelector from '../components/AgentSelector';
import CalendarView from '../components/CalendarView';
import BookingForm from '../components/BookingForm';

function BookingPage() {
  const { linkId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const embed = searchParams.get('embed') === 'true';
  const rescheduleMessage = location.state?.message;

  const [step, setStep] = useState(1);
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [linkData, setLinkData] = useState(null);
  const [agentLocked, setAgentLocked] = useState(false);

  useEffect(() => {
    initializePage();
  }, [linkId]);

  const initializePage = async () => {
    try {
      setLoading(true);
      setError(null);

      // If we have a linkId, fetch the link details first
      if (linkId) {
        console.log('BookingPage: Fetching link details for linkId:', linkId);
        const link = await api.getBookingLink(linkId);
        console.log('BookingPage: Link data received:', link);
        setLinkData(link);

        // If link has a locked agent, skip agent selection
        if (link.lockedAgentId && link.lockedAgent) {
          console.log('BookingPage: Agent locked, skipping to time selection');
          setSelectedAgent(link.lockedAgent);
          setAgentLocked(true);
          setStep(2); // Skip to time selection
          setLoading(false);
          return;
        }
      }

      // Otherwise, load all agents for selection
      console.log('BookingPage: Loading agents for selection');
      const agentsData = await api.getAgents();
      console.log('BookingPage: Agents loaded:', agentsData.length);
      setAgents(agentsData);
      setLoading(false);
    } catch (err) {
      console.error('BookingPage: Error initializing page:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to load booking information. Please try again.';
      console.error('BookingPage: Error message:', errorMessage);
      setError(errorMessage);
      setLoading(false);
    }
  };

  const handleAgentSelect = (agent) => {
    setSelectedAgent(agent);
    setStep(2);
  };

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot);
    setStep(3);
  };

  const handleBookingSubmit = async (formData) => {
    try {
      const bookingData = {
        agentId: selectedAgent.agentId,
        startTime: selectedSlot.startTime,
        duration: selectedSlot.duration,
        customerName: formData.name,
        customerEmail: formData.email,
        customerPhone: formData.phone,
        notes: formData.notes,
        linkId: linkId || null,
        postBookingWebhook: searchParams.get('post_booking_webhook') || null,
        webhookParams: Object.fromEntries(
          [...searchParams.entries()].filter(([k]) => !['embed', 'post_booking_webhook'].includes(k))
        ),
      };

      const booking = await api.createBooking(bookingData);
      navigate(`/bookings/${booking.bookingId}${embed ? '?embed=true' : ''}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create booking. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className={`${embed ? 'p-4' : 'min-h-screen bg-gray-50'} flex items-center justify-center`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading booking information...</p>
          {linkId && !embed && <p className="mt-2 text-sm text-gray-500">Link ID: {linkId}</p>}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${embed ? 'p-4' : 'min-h-screen bg-gray-50'} flex items-center justify-center px-4`}>
        <div className="max-w-md w-full">
          <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6 text-center">
            <div className="text-red-600 text-5xl mb-4">⚠️</div>
            <h2 className="text-xl font-semibold text-red-900 mb-2">Unable to Load Booking</h2>
            <p className="text-red-700 mb-4">{error}</p>
            {linkId && !embed && (
              <p className="text-sm text-red-600 mb-4">Link ID: {linkId}</p>
            )}
            <button
              onClick={() => window.location.reload()}
              className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={embed ? 'p-2' : 'min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8'}>
      <div className={embed ? '' : 'max-w-4xl mx-auto'}>
        {!embed && (
          <>
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-900">Schedule an Appointment</h1>
              <p className="mt-2 text-gray-600">
                {agentLocked
                  ? 'Pick a time and provide your details'
                  : 'Choose your host, pick a time, and provide your details'}
              </p>
            </div>

            {rescheduleMessage && (
              <div className="mb-6 bg-primary-50 border border-primary-200 text-primary-800 px-4 py-3 rounded-lg text-center">
                {rescheduleMessage}
              </div>
            )}

            {/* Progress Steps */}
            <div className="mb-8">
              <div className="flex items-center justify-center">
                <div className="flex items-center">
                  <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                    step >= 1 ? 'bg-primary-600 text-white' : 'bg-gray-300 text-gray-600'
                  }`}>
                    1
                  </div>
                  <span className={`ml-2 mr-8 text-sm font-medium ${
                    step >= 1 ? 'text-gray-900' : 'text-gray-500'
                  }`}>
                    Select Agent
                  </span>
                </div>

                <div className="flex items-center">
                  <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                    step >= 2 ? 'bg-primary-600 text-white' : 'bg-gray-300 text-gray-600'
                  }`}>
                    2
                  </div>
                  <span className={`ml-2 mr-8 text-sm font-medium ${
                    step >= 2 ? 'text-gray-900' : 'text-gray-500'
                  }`}>
                    Pick Time
                  </span>
                </div>

                <div className="flex items-center">
                  <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                    step >= 3 ? 'bg-primary-600 text-white' : 'bg-gray-300 text-gray-600'
                  }`}>
                    3
                  </div>
                  <span className={`ml-2 text-sm font-medium ${
                    step >= 3 ? 'text-gray-900' : 'text-gray-500'
                  }`}>
                    Your Details
                  </span>
                </div>
              </div>
            </div>
          </>
        )}

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        <div className={embed ? '' : 'bg-white rounded-lg shadow-md p-6'}>
          {step === 1 && (
            <AgentSelector
              agents={agents}
              onSelectAgent={handleAgentSelect}
            />
          )}

          {step === 2 && selectedAgent && (
            <>
              {agentLocked && !embed && (
                <div className="mb-4 bg-primary-50 border border-primary-200 text-primary-800 px-4 py-3 rounded">
                  Your meeting is with <strong>{selectedAgent.name}</strong>.
                  {linkData?.ticketId && <span> (Ticket #{linkData.ticketId})</span>}
                </div>
              )}
              <CalendarView
                agent={selectedAgent}
                duration={linkData?.duration || 30}
                onSelectSlot={handleSlotSelect}
                onBack={agentLocked ? null : () => setStep(1)}
                embed={embed}
              />
            </>
          )}

          {step === 3 && selectedAgent && selectedSlot && (
            <BookingForm
              agent={selectedAgent}
              slot={selectedSlot}
              meetingType={linkData?.meetingType || 'phone'}
              onSubmit={handleBookingSubmit}
              onBack={() => setStep(2)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default BookingPage;
